import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { routeTree } from './routeTree.gen';
import { useAuthStore } from './stores/authStore';
import { ThemeProvider } from './providers/ThemeProvider';
import { DesktopWrapper } from './components/desktop/DesktopWrapper';
import './styles/globals.css';

// Create a new router instance
const router = createRouter({ 
  routeTree,
  context: {
    auth: undefined!,
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 3,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const auth = useAuthStore();

  // Initialize auth on app start
  React.useEffect(() => {
    auth.initialize();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <DesktopWrapper>
        <RouterProvider router={router} context={{ auth }} />
        <ReactQueryDevtools initialIsOpen={false} />
      </DesktopWrapper>
    </QueryClientProvider>
  );
}

const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </React.StrictMode>
  );
}
